<script setup lang="ts">
    import { useBreakpoints } from '@vueuse/core';
    import TeaserElementDesktopClipPath from './TeaserElementDesktopClipPath.vue';
    import TeaserElementMobileClipPath from './TeaserElementMobileClipPath.vue';
    import type { SbBlokData, StoryblokRichTextNode } from '@storyblok/js';

    type TeaserLayout = TeaserElementStoryblok['layout'] | 'product-of-the-month';

    export type ExtendedTeaserElementStoryblok = Omit<TeaserElementStoryblok, 'layout'> & {
        layout: TeaserLayout;
    };

    const { blok } = defineProps<{ blok: ExtendedTeaserElementStoryblok }>();

    const breakpoints = useBreakpoints({ mobile: 640, desktop: 1024 }, { ssrWidth: 640 });
    const isDesktop = breakpoints.greaterOrEqual('desktop');
    const isMobile = breakpoints.smallerOrEqual('mobile');
    const useSmallButton = computed(() => blok.smallVersion === true && isMobile.value);

    const { theme: themeName, isNephrology, isUrology } = usePlatformMeta();
    const circularMediaClipPath = computed(() => {
        return isUrology.value || isNephrology.value;
    });

    const getMediaGradientAngle = () => {
        if (isDesktop.value) {
            return blok.imageRight ? '320deg' : '180deg';
        }

        if (blok.imageRight) {
            return '180deg';
        }

        return '80deg';
    };

    const getContentGradientAngle = () => {
        if (isDesktop.value) {
            return blok.imageRight ? '0deg' : '265deg';
        }

        if (blok.imageRight) {
            return '180deg';
        }

        return '0deg';
    };

    const loyaltyPointsBadge = computed(() => {
        return (
            (blok.loyaltyPointsBadge?.length &&
                blok.loyaltyPointsBadge?.length > 0 &&
                (blok.loyaltyPointsBadge?.[0] as unknown as SbBlokData)) ??
            null
        );
    });
</script>

<template>
    <div
        v-editable="blok"
        class="teaser-shell"
    >
        <div
            class="teaser-container"
            :class="{
                [themeName]: true,
                [blok.layout]: true,
                '--desktop-margin': blok.marginDesktop,
                '--vertical-align': blok.verticalAlign,
                '--round': blok.roundedEdges,
                '--right': blok.imageRight,
                '--left': !blok.imageRight,
                '--bottom-align-link': blok.bottomAlignLink
            }"
        >
            <template v-if="loyaltyPointsBadge">
                <StoryblokComponent :blok="loyaltyPointsBadge" />
            </template>
            <div
                class="teaser-content"
                :class="{
                    column: blok.layout === 'product-of-the-month',
                    right: blok.imageRight,
                    left: !blok.imageRight,
                    'small-version': blok.smallVersion
                }"
            >
                <div
                    class="media-wrapper"
                    :class="{ 'circular-media': circularMediaClipPath }"
                >
                    <template v-if="!circularMediaClipPath">
                        <TeaserElementDesktopClipPath :image-right="Boolean(blok.imageRight)" />
                        <TeaserElementMobileClipPath :image-right="Boolean(blok.imageRight)" />
                    </template>

                    <div class="image-wrapper">
                        <StoryblokImage
                            :media="blok.media"
                            type="mainPicture"
                            :alt="blok.media.alt ? blok.media.alt : ''"
                            :eager="blok.layout === 'shop'"
                        />
                        <slot name="training-points" />
                    </div>

                    <GradientShape
                        v-if="blok && blok.layout !== 'product-of-the-month'"
                        :shape="isDesktop ? 'leaf' : 'circle'"
                        :theme="blok.layout"
                        :gradient-angle="getMediaGradientAngle()"
                    />
                </div>

                <div
                    class="content-wrapper"
                    :class="{ 'small-version': blok.smallVersion }"
                >
                    <h2>
                        <span
                            v-if="blok.subheadline"
                            class="sub-header"
                            >{{ blok.subheadline }}</span
                        >
                        {{ blok.headline }}
                    </h2>

                    <div
                        class="teaser-text"
                        :class="{ 'small-version': blok.smallVersion }"
                    >
                        <RichText :doc="blok.text as StoryblokRichTextNode<string>" />
                    </div>

                    <StoryblokComponentWrapper
                        v-for="button in blok.button"
                        :key="button._uid"
                        :blok="button as unknown as SbBlokData"
                        :size="useSmallButton ? 'small' : 'default'"
                        :stretch="blok.layout === 'product-of-the-month'"
                    />

                    <GradientShape
                        v-if="blok.layout !== 'product-of-the-month'"
                        :shape="isDesktop ? 'circle' : 'leaf'"
                        :theme="blok.layout"
                        :gradient-angle="getContentGradientAngle()"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/teaser/teaser-element.scss';
</style>
